import _ from 'lodash';
import { getIntl } from 'umi';

import { selectCommercialBillingApiRoute } from '@/components/SiderMenu/utils/sessionNavigation';
import { initialPagination, initialState, makeListReducers } from '@/models/utils';
import { bindServices } from '@/services/api';
import { CoreEnums } from '@/utils/domain/constants';
import { getFiltersGlobalFilters, toPaginate } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';

import {
  AGREEMENT_NEW_VERSION_ACTION,
  AGREEMENT_REVIEW_ACTION,
  DEFAULT_ORDER_BY,
  MODEL_NAME,
  NAVIGATION_KEY,
} from './constants';
import { filtersConfig, toRegistrationApprovalPolicyApiValue } from './utils';

const { BulkActionType } = CoreEnums;
const api = bindServices(MODEL_NAME);
const pagedQuery = CoreQueries.ChannelAgreement.paged;

/** Commercial billing CRUD — API base from session navigation (dynamic route). */
const moduleInitialState = {
  ...initialState,
  list: [],
  orderBy: DEFAULT_ORDER_BY,
  select: pagedQuery.select,
  searchSelect: pagedQuery.searchSelect,
  showForm: false,
  detail: false,
  id: null,
  item: null,
  rules: [],
  openRulesDrawer: false,
  rulesDrawerItem: null,
  reviewModal: { state: false, id: null, action: null },
  versionModal: { state: false, id: null },
  success: false,
  activatingItemIds: {},
  filtersConfig,
};

const ChannelAgreementsModel = {
  namespace: MODEL_NAME,
  state: moduleInitialState,

  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute) return;

        const pagination = yield select((state) => state[MODEL_NAME].pagination);
        const pageSize = yield select((state) => state.global.pageSize);
        const res = yield call(api.getAll, {
          route: `${fetchRoute}/paged`,
          params: {
            limit: pageSize,
            ...pagedQuery,
            ...pagination,
            ...payload,
          },
          readPriority: true,
        });

        const items = res?.items || res?.data || [];
        yield put({
          type: 'queryData',
          payload: {
            ...res,
            items,
            limit: pageSize,
            page: payload.page,
            totalCount: res?.totalCount ?? res?.count ?? res?.total ?? items.length,
          },
        });
      } catch (e) {
        /*  */
      }
    },

    *fetchItem({ payload }, { call, put, select }) {
      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute) return;

        yield put({ type: 'success', payload: { success: false } });
        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: {
            ...(payload.detail ? CoreQueries.ChannelAgreement?.itemDetail : {}),
            ...(payload?.params && { ...payload.params }),
          },
        });
        const item = Array.isArray(res) ? res[0] : res;
        yield put({ type: 'queryItem', payload: item });
      } catch (e) {
        /*  */
      }
    },

    *fetchCreate({ payload }, { put, call, select }) {
      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute) return;

        yield put({ type: 'success', payload: { success: false } });
        const data = {
          ...payload.data,
          rules: Array.isArray(payload.data?.rules) ? payload.data.rules : [],
        };
        yield call(api.create, { route: fetchRoute, data });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0]?.field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: { validationErrors: e.validationErrors || [], message: e.message },
          });
        }
      }
    },

    *fetchUpdate({ payload }, { put, call, select }) {
      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute) return;

        yield put({ type: 'success', payload: { success: false } });
        // rules: null = leave existing rules unchanged (API contract).
        const data = {
          ...payload.data,
          rules: payload.data?.rules === undefined ? null : payload.data.rules,
        };
        yield call(api.update, { route: fetchRoute, data });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0]?.field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: { validationErrors: e.validationErrors || [], message: e.message },
          });
        }
      }
    },

    *fetchDelete({ payload }, { call, put, select }) {
      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute) return;

        const { id, view } = payload;
        yield call(api.remove, { route: fetchRoute, id });
        if (view) {
          yield put({
            type: 'queryShowForm',
            payload: { state: false, item: null, detail: false, id: null },
          });
        }
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({ type: 'global/modalConfirm', payload: { state: false, id: null } });
      }
    },

    *fetchActivate({ payload }, { put, call, select }) {
      const { id } = payload;
      yield put({ type: 'queryActivatingItem', payload: { id, active: true } });
      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute) return;

        yield call(api.activate, { route: fetchRoute, id, isActive: payload.state });
        yield put({ type: 'channelAgreementActivate', payload });
      } catch (e) {
        /*  */
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { id, active: false } });
      }
    },

    *bulkDeleteChannelAgreements({ payload }, { call, put, select }) {
      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute) return;

        const { ids } = payload;
        if (!ids || ids.length === 0) return;
        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Delete,
          ids,
        });

        const pageSize = yield select((state) => state.global.pageSize);
        const stateModel = yield select((state) => state[MODEL_NAME]);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, MODEL_NAME);

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({
          type: 'fetchList',
          payload: {
            ...pagedQuery,
            page: 1,
            limit: pageSize,
            ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
            ...(stateModel.orderBy && { orderBy: stateModel.orderBy }),
            ...(filters && { filter: filters }),
            select: stateModel.select,
          },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *bulkActivateChannelAgreements({ payload }, { call, put, select }) {
      const { ids } = payload;
      if (!ids || ids.length === 0) return;
      yield put({ type: 'queryActivatingItem', payload: { ids, active: true } });
      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute) return;

        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Activate,
          ids,
        });
        yield put({
          type: 'bulkChannelAgreementActivate',
          payload: { ids, isActive: true },
        });
      } catch (e) {
        /*  */
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { ids, active: false } });
      }
    },

    *bulkDeactivateChannelAgreements({ payload }, { call, put, select }) {
      const { ids } = payload;
      if (!ids || ids.length === 0) return;
      yield put({ type: 'queryActivatingItem', payload: { ids, active: true } });
      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute) return;

        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Deactivate,
          ids,
        });
        yield put({
          type: 'bulkChannelAgreementActivate',
          payload: { ids, isActive: false },
        });
      } catch (e) {
        /*  */
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { ids, active: false } });
      }
    },

    *showRulesDrawer({ payload }, { put }) {
      yield put({ type: 'queryShowRulesDrawer', payload });
      if (!payload?.state) return;
      if (payload.item) {
        yield put({ type: 'queryRulesDrawerItem', payload: payload.item });
        return;
      }
      if (payload.id) {
        yield put({ type: 'fetchRulesDrawerItem', payload: { id: payload.id } });
      }
    },

    *fetchRulesDrawerItem({ payload }, { call, put, select }) {
      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute || !payload?.id) return;

        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: CoreQueries.ChannelAgreement?.itemDetail || {},
        });
        const item = Array.isArray(res) ? res[0] : res;
        yield put({ type: 'queryRulesDrawerItem', payload: item });
      } catch (e) {
        /*  */
      }
    },

    *showReviewModal({ payload }, { put }) {
      yield put({ type: 'queryShowReviewModal', payload });
    },

    *closeReviewModal(_, { put }) {
      yield put({ type: 'queryShowReviewModal', payload: { state: false, id: null } });
    },

    *showVersionModal({ payload }, { put }) {
      yield put({ type: 'queryShowVersionModal', payload });
    },

    *closeVersionModal(_, { put }) {
      yield put({ type: 'queryShowVersionModal', payload: { state: false, id: null } });
    },

    *createAgreementVersion({ payload }, { call, put, select }) {
      const id = payload?.id;
      if (!id) return;

      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute) return;

        yield call(api.apiRoute, {
          route: `${fetchRoute}/${id}/${AGREEMENT_NEW_VERSION_ACTION}`,
          method: 'post',
        });

        yield put({ type: 'queryShowVersionModal', payload: { state: false, id: null } });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        const status = e?.status || e?.response?.status || e?.data?.status;
        if (status === 409) {
          yield put({ type: 'queryShowVersionModal', payload: { state: false, id: null } });
          yield put({ type: 'fetchList', payload: { page: 1 } });
          const currentId = yield select((state) => state[MODEL_NAME].id);
          if (currentId) {
            yield put({
              type: 'fetchItem',
              payload: { id: currentId, detail: true },
            });
          }
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'error',
              message: getIntl().formatMessage({ id: 'channelAgreement.version.conflict' }),
            },
          });
          return;
        }
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
    },

    *changeSuccess({ payload }, { put }) {
      yield put({ type: 'success', payload });
    },

    *reviewAgreement({ payload }, { call, put, select }) {
      const { id, action, reviewNotes, registrationApprovalPolicy } = payload || {};
      const isApprove = action === AGREEMENT_REVIEW_ACTION.APPROVE;
      const isReject = action === AGREEMENT_REVIEW_ACTION.REJECT;
      if (!id || (!isApprove && !isReject)) return;

      // Reset before the API call so ConfigListPage sees a false → true
      // transition even if a previous create/update left success stuck true.
      yield put({ type: 'success', payload: { success: false } });

      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute) return;

        const notes = String(reviewNotes || '').trim();
        const data = {};
        if (notes) {
          data.reviewNotes = notes;
        }
        if (isApprove) {
          const policy = toRegistrationApprovalPolicyApiValue(registrationApprovalPolicy);
          if (!policy) return;
          data.registrationApprovalPolicy = policy;
        }

        yield call(api.apiRoute, {
          route: `${fetchRoute}/${id}/${action}`,
          method: 'post',
          data,
        });

        const approvalStatus = isApprove
          ? CoreEnums.ChannelAgreementApprovalStatus.Approved
          : CoreEnums.ChannelAgreementApprovalStatus.Rejected;

        yield put({ type: 'queryShowReviewModal', payload: { state: false, id: null } });
        yield put({ type: 'channelAgreementReview', payload: { id, approvalStatus } });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
    },

    *setOrderBy({ payload }, { put }) {
      yield put({
        type: 'queryOrderBy',
        payload,
      });
    },

    *showForm({ payload }, { put }) {
      yield put({
        type: 'queryShowForm',
        payload,
      });
      if (!_.isNil(payload.id)) {
        yield put({ type: 'editOrDetail', payload });
      }
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },
  },

  reducers: {
    ...makeListReducers({
      initialPagination,
      initialState: moduleInitialState,
    }),
    queryData(state, { payload }) {
      return {
        ...state,
        list: payload.items,
        pagination: _.merge(state.pagination, toPaginate(payload)),
      };
    },
    queryItem(state, { payload }) {
      return {
        ...state,
        item: payload,
        rules: Array.isArray(payload?.rules) ? payload.rules : [],
      };
    },
    queryShowRulesDrawer(state, { payload }) {
      return {
        ...state,
        openRulesDrawer: !!payload?.state,
        ...(!payload?.state && { rulesDrawerItem: null }),
      };
    },
    queryRulesDrawerItem(state, { payload }) {
      return {
        ...state,
        rulesDrawerItem: payload || null,
      };
    },
    queryShowReviewModal(state, { payload }) {
      return {
        ...state,
        reviewModal: {
          state: !!payload?.state,
          id: payload?.id ?? null,
          action: payload?.action ?? null,
        },
      };
    },
    queryShowVersionModal(state, { payload }) {
      return {
        ...state,
        versionModal: {
          state: !!payload?.state,
          id: payload?.id ?? null,
        },
      };
    },
    queryShowForm(state, { payload }) {
      return {
        ...state,
        showForm: payload.state,
        openView: payload.state ? false : state.openView,
        detail: payload.detail,
        id: payload.id,
        item: payload.item ?? null,
        success: false,
        validationErrors: [],
        ...(!payload.state && { rules: [] }),
      };
    },
    editOrDetail(state, { payload }) {
      return { ...state, item: payload.item ?? {} };
    },
    queryActivatingItem(state, { payload }) {
      const { id, ids, active } = payload || {};
      const activatingItemIds = { ...(state.activatingItemIds || {}) };

      if (Array.isArray(ids) && ids.length) {
        ids.forEach((itemId) => {
          if (itemId == null) return;
          if (active) {
            activatingItemIds[itemId] = true;
          } else {
            delete activatingItemIds[itemId];
          }
        });
        return { ...state, activatingItemIds };
      }

      if (id == null) {
        return { ...state, activatingItemIds: {} };
      }

      if (active) {
        activatingItemIds[id] = true;
      } else {
        delete activatingItemIds[id];
      }
      return { ...state, activatingItemIds };
    },
    channelAgreementActivate(state, { payload }) {
      const list = state.list.map((item) =>
        item.id === payload.id ? { ...item, isActive: payload.state } : item,
      );
      return { ...state, list };
    },
    channelAgreementReview(state, { payload }) {
      const { id, approvalStatus } = payload || {};
      if (id == null || approvalStatus == null) return state;
      const list = (state.list || []).map((item) =>
        item.id === id ? { ...item, approvalStatus } : item,
      );
      const item = state.item?.id === id ? { ...state.item, approvalStatus } : state.item;
      return { ...state, list, item };
    },
    bulkChannelAgreementActivate(state, { payload }) {
      const { ids, isActive } = payload;
      const idSet = new Set(ids);
      const list = state.list.map((item) => (idSet.has(item.id) ? { ...item, isActive } : item));
      return { ...state, list };
    },
    setFiltersConfig(state, { payload }) {
      return {
        ...state,
        filtersConfig: payload,
      };
    },
  },
};

export default ChannelAgreementsModel;
